import { useEffect, useState } from "react";
import { ArrowUp, ArrowDown } from "lucide-react";
import { OverviewService } from "../../services/api/overview.service";
import ClinicPlaceholder from "../../assets/images/default_avatar.jpg";

interface Clinic {
  id: string;
  name: string;
  supervisorName: string;
  mobileNumber: string;
  address: string;
  revenue: number;
  profileImage: string;
}

const PerformanceTable = () => {
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [filteredClinics, setFilteredClinics] = useState<Clinic[]>([]);
  const [filterType, setFilterType] = useState<"all" | "high" | "low">("all");

  useEffect(() => {
    async function fetchClinics() {
      const response = await OverviewService.getAdminClinicPerformance("desc");
      if (Array.isArray(response)) {
        setClinics(response);
        setFilteredClinics(response); // Initialize filtered clinics
      } else {
        setClinics([]);
        setFilteredClinics([]);
      }
    }
    fetchClinics();
  }, []);

  useEffect(() => {
    const sortedClinics = [...clinics].sort((a, b) => b.revenue - a.revenue);
    if (filterType === "high") {
      const highPerforming = sortedClinics.filter((clinic) => clinic.revenue > 0);
      setFilteredClinics(highPerforming.length > 0 ? highPerforming : []);
    } else if (filterType === "low") {
      const lowPerforming = sortedClinics.filter((clinic) => clinic.revenue === 0);
      setFilteredClinics(lowPerforming.length > 0 ? lowPerforming : []);
    } else {
      setFilteredClinics(sortedClinics);
    }
  }, [clinics, filterType]);

  return (
    <div className="p-2 bg-white border border-gray-400/60 rounded-md flex items-center justify-center flex-col gap-2 w-full">
      <div className="p-6 w-full mx-auto">
        <h1 className="text-xl font-semibold mb-4">Clinic Performance</h1>
        <div className="flex gap-6">
          <button onClick={() => setFilterType("all")} className="text-sm text-blue-500">Show All</button>
          <button onClick={() => setFilterType("high")} className="flex items-center gap-2">
            <ArrowUp className="text-green-500" size={16} />
            <span className={`text-sm ${filterType === "high" ? "text-green-600" : "text-green-500"}`}>High Performing</span>
          </button>
          <button onClick={() => setFilterType("low")} className="flex items-center gap-2">
            <ArrowDown className="text-red-500" size={16} />
            <span className={`text-sm ${filterType === "low" ? "text-red-600" : "text-red-500"}`}>Low Performing</span>
          </button>
        </div>
        <div className="bg-gray-50 p-4 rounded-t-lg mt-4">
          <div className="grid grid-cols-5 gap-4">
            <div className="text-gray-600">Clinic Name</div>
            <div className="text-gray-600">Supervisor</div>
            <div className="text-gray-600">Mobile</div>
            <div className="text-gray-600">Revenue</div>
            <div className="text-gray-600">Address</div>
          </div>
        </div>
        <div className="divide-y divide-gray-200">
          {filteredClinics.length > 0 ? (
            filteredClinics.map((clinic) => (
              <div key={clinic.id} className="py-4 grid grid-cols-5 gap-4 items-center">
                <div className="flex items-center gap-3">
                  <img src={clinic.profileImage || ClinicPlaceholder} className="w-10 h-10 rounded-full" alt="Clinic Profile" />
                  <span className="font-medium">{clinic.name}</span>
                </div>
                <div className="ml-2">{clinic.supervisorName}</div>
                <div>{clinic.mobileNumber}</div>
                <div className="font-bold">₹{clinic.revenue}</div>
                <div className="text-gray-500 text-sm text-justify">{clinic.address}</div>
              </div>
            ))
          ) : (
            <div className="text-center py-4 text-gray-500">No {filterType === "high" ? "high-performing" : "low-performing"} clinics available</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PerformanceTable;
